import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TelegramIcon from "@mui/icons-material/Telegram";
import EmailIcon from "@mui/icons-material/Email";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import SettingsPhoneIcon from "@mui/icons-material/SettingsPhone";
import MusicVideoIcon from "@mui/icons-material/MusicVideo";
import SmsIcon from "@mui/icons-material/Sms";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GoogleIcon from "@mui/icons-material/Google";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
// AccessTimeIcon removed - no more "coming soon" state
import MetaSignupFlow from "../MetaSignupFlow";
import TelegramConnect from "../TelegramConnect";
import EmailConnect from "../EmailConnect";
import { hubApi } from "../../services/hubApi";

const channels = [
  {
    id: "whatsapp",
    planKey: "whatsapp",
    name: "WhatsApp",
    subtitle: "Coexistencia",
    icon: WhatsAppIcon,
    color: "#25D366",
  },
  {
    id: "facebook",
    planKey: "facebook",
    name: "Facebook",
    subtitle: "Messenger",
    icon: FacebookIcon,
    color: "#1877F2",
  },
  {
    id: "instagram",
    planKey: "instagram",
    name: "Instagram",
    subtitle: "Direct Messages",
    icon: InstagramIcon,
    color: "#E4405F",
  },
  {
    id: "telegram",
    planKey: "telegram",
    name: "Telegram",
    subtitle: "Bot API",
    icon: TelegramIcon,
    color: "#0088CC",
  },
  {
    id: "email",
    planKey: "email",
    name: "E-mail",
    subtitle: "SMTP + IMAP",
    icon: EmailIcon,
    color: "#FF6B00",
  },
  {
    id: "voip",
    planKey: "voip",
    name: "OtimizAPP VoIP",
    subtitle: "Chamadas de Voz",
    icon: PhoneInTalkIcon,
    color: "#8B5CF6",

  },
  {
    id: "sip",
    planKey: "sip",
    name: "SIP Trunk",
    subtitle: "Linhas VoIP",
    icon: SettingsPhoneIcon,
    color: "#06B6D4",

  },
  {
    id: "tiktok",
    planKey: "tiktok",
    name: "TikTok",
    subtitle: "Mensagens Diretas",
    icon: MusicVideoIcon,
    color: "#000000",

  },
  {
    id: "sms",
    planKey: "sms",
    name: "SMS",
    subtitle: "Twilio / Vonage",
    icon: SmsIcon,
    color: "#43A047",

  },
  {
    id: "google_business",
    planKey: "google_business",
    name: "Google Business",
    subtitle: "Business Messages",
    icon: GoogleIcon,
    color: "#4285F4",

  },
  {
    id: "mercadolivre",
    planKey: "mercadolivre",
    name: "Mercado Livre",
    subtitle: "Perguntas e Mensagens",
    icon: StorefrontIcon,
    color: "#FFE600",

  },
  {
    id: "teams",
    planKey: "teams",
    name: "Microsoft Teams",
    subtitle: "Chat Corporativo",
    icon: GroupsIcon,
    color: "#6264A7",

  },
];

/**
 * Determina o estado do card baseado no plano do tenant
 * @returns "active" | "purchase"
 */
function getChannelState(channel, plan) {
  if (!plan) return "purchase";
  return plan[channel.planKey] === true ? "active" : "purchase";
}

export default function HubModal({ open, onClose, onConnectionCreated, tenant, plan }) {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [redirecting, setRedirecting] = useState(null);
  const [marketplaceUrl, setMarketplaceUrl] = useState(null); // iframe URL
  const [iframeLoading, setIframeLoading] = useState(false);

  const handleClose = () => {
    setSelectedChannel(null);
    setMarketplaceUrl(null);
    setIframeLoading(false);
    onClose();
  };

  const handleBack = () => {
    if (marketplaceUrl) {
      setMarketplaceUrl(null);
      setIframeLoading(false);
      return;
    }
    setSelectedChannel(null);
  };

  const handleSuccess = () => {
    setSelectedChannel(null);
    setMarketplaceUrl(null);
    onConnectionCreated();
  };

  // Escutar mensagens do iframe do portal (ex: plano ativado, fechar)
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (!marketplaceUrl) return;
      const { data } = event;
      if (data?.type === "hub-plan-activated" || data?.type === "hub-checkout-complete") {
        // Portal sinalizou que canal foi ativado
        setMarketplaceUrl(null);
        setIframeLoading(false);
        onConnectionCreated(); // refresh plan/connections
      }
      if (data?.type === "hub-marketplace-close") {
        setMarketplaceUrl(null);
        setIframeLoading(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [marketplaceUrl, onConnectionCreated]);

  const handleCardClick = async (channel) => {
    const state = getChannelState(channel, plan);

    if (state === "purchase") {
      // Abrir marketplace como iframe dentro do modal
      setRedirecting(channel.id);
      setIframeLoading(true);
      try {
        const { data } = await hubApi.getPortalRedirect(channel.id);
        if (data.redirectUrl) {
          // Adicionar param embed=1 para o portal saber que esta em iframe
          const url = new URL(data.redirectUrl);
          url.searchParams.set("embed", "1");
          setMarketplaceUrl(url.toString());
        }
      } catch (err) {
        console.error("Portal redirect error:", err);
        setIframeLoading(false);
      }
      setRedirecting(null);
      return;
    }

    // Canal ativo — abrir fluxo de conexão
    setSelectedChannel(channel.id);
  };

  const renderChannelFlow = () => {
    switch (selectedChannel) {
      case "whatsapp":
      case "facebook":
      case "instagram":
        return (
          <MetaSignupFlow
            channel={selectedChannel}
            onBack={handleBack}
            onSuccess={handleSuccess}
          />
        );
      case "telegram":
        return (
          <TelegramConnect onBack={handleBack} onSuccess={handleSuccess} />
        );
      case "email":
        return (
          <EmailConnect onBack={handleBack} onSuccess={handleSuccess} />
        );
      default:
        return null;
    }
  };

  const renderBadge = (channel) => {
    const state = getChannelState(channel, plan);

    if (state === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Ativo"
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(37, 211, 102, 0.15)",
            color: "#25D366",
            fontSize: "0.65rem",
            fontWeight: 700,
            height: 22,
            "& .MuiChip-icon": { color: "#25D366" },
          }}
        />
      );
    }

    if (state === "purchase") {
      return (
        <Chip
          icon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
          label="Contratar"
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(255, 107, 0, 0.15)",
            color: "#FF6B00",
            fontSize: "0.65rem",
            fontWeight: 700,
            height: 22,
            "& .MuiChip-icon": { color: "#FF6B00" },
          }}
        />
      );
    }

    return null;
  };

  const isMarketplace = !!marketplaceUrl;
  const showBackBtn = selectedChannel || isMarketplace;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isMarketplace ? "lg" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#111827",
          backgroundImage: "none",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.06)",
          overflow: "hidden",
          minHeight: isMarketplace ? "85vh" : selectedChannel ? 400 : "auto",
          maxHeight: isMarketplace ? "90vh" : "auto",
          transition: "all 0.3s ease",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          px: 3,
          pt: 2.5,
          pb: isMarketplace ? 1 : 0,
          borderBottom: isMarketplace ? "1px solid rgba(255,255,255,0.06)" : "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {showBackBtn && (
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                color: "#9CA3AF",
                bgcolor: "rgba(255,255,255,0.05)",
                "&:hover": { color: "#F3F4F6", bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </IconButton>
          )}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: isMarketplace ? 0 : 1.5 }}>
              <Box
                component="img"
                src="/gateway-icon.png"
                alt="OtimizApp HUB"
                sx={{ width: 24, height: 24, borderRadius: "6px", objectFit: "cover" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "#FF6B00",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {isMarketplace ? "MARKETPLACE" : "OtimizAPP HUB"}
              </Typography>
            </Box>
            {!isMarketplace && (
              <>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#F3F4F6", lineHeight: 1.2 }}
                >
                  {selectedChannel
                    ? `Conectar ${channels.find((c) => c.id === selectedChannel)?.name}`
                    : "Hub de Conexoes"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#9CA3AF", mt: 0.5, fontSize: "0.85rem" }}
                >
                  {selectedChannel
                    ? "Siga os passos para configurar"
                    : "Selecione um canal para conectar"}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#6B7280", mt: 0.5, "&:hover": { color: "#F3F4F6" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      {isMarketplace ? (
        /* Marketplace iframe */
        <Box sx={{ flex: 1, position: "relative", minHeight: "75vh" }}>
          {iframeLoading && (
            <Box sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#111827",
              zIndex: 2,
            }}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress sx={{ color: "#FF6B00", mb: 2 }} />
                <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                  Carregando marketplace...
                </Typography>
              </Box>
            </Box>
          )}
          <iframe
            src={marketplaceUrl}
            title="OtimizAPP Marketplace"
            onLoad={() => setIframeLoading(false)}
            style={{
              width: "100%",
              height: "75vh",
              border: "none",
              borderRadius: "0 0 16px 16px",
              background: "#fff",
            }}
          />
        </Box>
      ) : (
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          {selectedChannel ? (
            renderChannelFlow()
          ) : (
            <Grid container spacing={1.5}>
              {channels.map((channel) => {
                const Icon = channel.icon;
                const state = getChannelState(channel, plan);
                const isDisabled = false;
                const isRedirecting = redirecting === channel.id;

                return (
                  <Grid item xs={6} sm={4} key={channel.id}>
                    <Card
                      sx={{
                        bgcolor: "#1E293B",
                        backgroundImage: "none",
                        border: "1px solid rgba(255, 255, 255, 0.04)",
                        borderRadius: "12px",
                        transition: "all 0.2s ease",
                        opacity: isDisabled ? 0.5 : 1,
                        position: "relative",
                        "&:hover": isDisabled
                          ? {}
                          : {
                              bgcolor: "#253347",
                              border: `1px solid ${channel.color}40`,
                              transform: "translateY(-2px)",
                              boxShadow: `0 4px 20px ${channel.color}10`,
                            },
                      }}
                    >
                      {renderBadge(channel)}
                      <CardActionArea
                        onClick={() => handleCardClick(channel)}
                        disabled={isDisabled || isRedirecting}
                        sx={{ p: 0 }}
                      >
                        <CardContent
                          sx={{
                            textAlign: "center",
                            py: 3,
                            px: 2,
                            "&:last-child": { pb: 3 },
                          }}
                        >
                          {isRedirecting ? (
                            <CircularProgress
                              size={40}
                              sx={{ color: channel.color, mb: 1.5 }}
                            />
                          ) : (
                            <Icon
                              sx={{
                                fontSize: 40,
                                color: channel.color,
                                mb: 1.5,
                              }}
                            />
                          )}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "#F3F4F6",
                              fontSize: "0.95rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {channel.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#9CA3AF",
                              fontSize: "0.75rem",
                              display: "block",
                              mt: 0.3,
                            }}
                          >
                            {isRedirecting
                              ? "Redirecionando..."
                              : state === "purchase"
                              ? "Clique para contratar"
                              : channel.subtitle}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Footer spacer */}
      {!selectedChannel && !isMarketplace && <Box sx={{ pb: 1.5 }} />}
    </Dialog>
  );
}
